/**
 * Centralized API endpoint configuration
 *
 * All API calls should use endpoints from this file instead of hardcoding URLs.
 * This ensures consistency and makes it easy to switch between environments.
 *
 * Environment variables:
 * - REACT_APP_API_URL: Base API URL (default: http://localhost:8000)
 *
 * Usage:
 * import API_ENDPOINTS from '../config/apiConfig';
 * const response = await fetch(API_ENDPOINTS.CONFIG.SAVE);
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const API_ENDPOINTS = {
  // ============ Configuration Endpoints ============
  // All config management operations
  CONFIG: {
    // List all configurations
    LIST: `${API_BASE_URL}/api/configs`,

    // Save a new configuration
    SAVE: `${API_BASE_URL}/api/configs/save`,

    // Get a specific configuration by ID
    GET: (id: string) => `${API_BASE_URL}/api/configs/${id}`,

    // Delete a configuration
    DELETE: (id: string) => `${API_BASE_URL}/api/configs/${id}`,

    // Validate configuration without saving
    VALIDATE: `${API_BASE_URL}/api/configs/validate`,

    // Get available columns from a dataset
    DATA_COLUMNS: (fileId: string) =>
      `${API_BASE_URL}/api/configs/data-columns/${fileId}`,

    // Export configuration as YAML
    EXPORT_YAML: `${API_BASE_URL}/api/configs/export-yaml`,

    // Import configuration from YAML
    IMPORT_YAML: `${API_BASE_URL}/api/configs/import-yaml`,
  },

  // ============ Dataset Endpoints ============
  // All dataset management operations
  DATASET: {
    // List all datasets
    LIST: `${API_BASE_URL}/api/datasets`,

    // Save/upload a new dataset
    SAVE: `${API_BASE_URL}/api/datasets/save`,

    // Get dataset metadata
    GET: (id: string) => `${API_BASE_URL}/api/datasets/${id}`,

    // Delete a dataset
    DELETE: (id: string) => `${API_BASE_URL}/api/datasets/${id}`,
  },

  // ============ Analysis Endpoints ============
  // All MMM analysis operations
  ANALYSIS: {
    // Run analysis job
    RUN: `${API_BASE_URL}/api/analysis/run`,

    // Check job status and progress
    STATUS: (jobId: string) =>
      `${API_BASE_URL}/api/analysis/status/${jobId}`,
  },

  // ============ Model Endpoints ============
  // All trained model operations
  MODEL: {
    // List all trained models
    LIST: `${API_BASE_URL}/api/v1/models`,

    // Get model metadata
    GET: (id: string) => `${API_BASE_URL}/api/v1/models/${id}`,

    // Delete a model
    DELETE: (id: string) => `${API_BASE_URL}/api/v1/models/${id}`,

    // Get complete visualization data for results display
    // This is the critical endpoint for displaying analysis results
    VISUALIZATION_DATA: (modelId: string) =>
      `${API_BASE_URL}/api/v1/models/${modelId}/visualization-data`,
  },

  // ============ Optimization Endpoints ============
  // Budget optimization and allocation
  OPTIMIZE: {
    // Calculate optimal budget allocation
    BUDGET: `${API_BASE_URL}/api/v1/optimize/budget`,
  },

  // ============ Scenario Endpoints ============
  // Scenario analysis and planning
  SCENARIO: {
    // Create and run scenario analysis
    ANALYZE: `${API_BASE_URL}/api/v1/scenarios`,
  },

  // ============ Diagnostic Endpoints ============
  DIAGNOSTICS: {
    PRE_TRAINING_CHECK: `${API_BASE_URL}/api/v1/pre-training-check`,
    QUICK_FIX_APPLY: `${API_BASE_URL}/api/v1/quick-fix/apply`,
    QUICK_FIX_ACTIONS: `${API_BASE_URL}/api/v1/quick-fix/actions`,
  },

  // ============ System Endpoints ============
  SYSTEM: {
    HEALTH: `${API_BASE_URL}/health`,
  },
};

export default API_ENDPOINTS;
